/**
 * Payment Methods Query Hook
 * 
 * React Query hook for fetching all saved payment methods for a user.
 * 
 * ## Features
 * - Type-safe query with error handling
 * - Automatic caching and refetching
 * - Loading and error states
 * - Authentication-aware (only fetches when user is logged in)
 * 
 * @module payment/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { paymentMethodService } from '~/services/internalApi/payment/api/paymentMethodService';
import { useAuth } from '~/services/auth';
import { useTenantHandle } from '~/services/appRuntime';

export interface UsePaymentMethodsQueryOptions {
  /** User ID (optional - will use auth context if not provided) */
  userId?: number | null;
  
  /** Whether the query is enabled (default: true if user is authenticated) */
  enabled?: boolean;
}

/**
 * Fetch all saved payment methods for a user
 * 
 * Returns the full React Query result with a convenience `paymentMethods` field.
 * 
 * @example
 * ```tsx
 * // Simple usage - convenience field
 * const { paymentMethods, isLoading } = usePaymentMethodsQuery();
 * 
 * if (isLoading) return <div>Loading payment methods...</div>;
 * 
 * return (
 *   <div>
 *     {paymentMethods && paymentMethods.length > 0 ? (
 *       <ul>
 *         {paymentMethods.map(pm => (
 *           <li key={pm.id}>
 *             {pm.cardBrand} •••• {pm.last4}
 *           </li>
 *         ))}
 *       </ul>
 *     ) : (
 *       <p>No saved payment methods</p>
 *     )}
 *   </div>
 * );
 * 
 * // Advanced usage - React Query features
 * const { paymentMethods, refetch, dataUpdatedAt } = usePaymentMethodsQuery();
 * ```
 */
export function usePaymentMethodsQuery({
  userId: providedUserId,
  enabled = true,
}: UsePaymentMethodsQueryOptions = {}) {
  const { user, isAuthenticated } = useAuth();
  const [tenantHandle] = useTenantHandle();
  
  // Use provided userId or fall back to authenticated user's ID
  // Handle both string and number user IDs from auth context
  const userId = providedUserId ?? (user?.id ? (typeof user.id === 'string' ? parseInt(user.id) : user.id) : null);

  const query = useQuery({
    // Use tenant-scoped query key (payment methods shared across clubs)
    queryKey: tenantHandle && userId ? queryKeys.tenant.paymentMethods(tenantHandle, userId) : queryKeys.disabled.paymentMethods(),
    queryFn: async () => {
      if (!userId) {
        return [];
      }

      const result = await paymentMethodService.getPaymentMethods(userId);
      return result.paymentMethods || [];
    },
    enabled: enabled && isAuthenticated && userId !== null && !!tenantHandle,
    // Stable data - payment methods don't change often
    ...cachePresets.stable,
  });

  return {
    ...query,
    paymentMethods: query.data ?? [],
  };
}
